import { Button, Divider, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu } from '@mui/material'
import { AppMenu } from '../../common/AppMenu'
import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { PriceScaleMode } from 'lightweight-charts'
import { ChartInvertScaleOptions, ChartPriceScaleOptions, EventsSettings, ScalesAndLinesSettings } from '../chart.typings'
import { selectChartConfig, selectChartEventsSettings, selectChartScalesAndLinesSettings } from '../../../selectors/chart'
import SettingsOutlinedIcon from '../../../assets/icons/SettingOutlined'
import { NonInteractiveTooltip } from '../../custom'
import { selectMultiPaneFullScreen } from '../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import ChartSettingsDialogs from '../../chartSettings/ChartSettingsDialogs'
import { ChartType } from '../../../proto/generated/chart'
import { selectIsEmbedded } from '../../../selectors/device'
import { callNative } from '../../../util/nativeBridge'
import { Check, ChevronRight } from '@mui/icons-material'
import { updateChartConfig } from '../../../reducer/chart.reducer'
import { isTransformedChartType } from '../../../util/chart'
import { defaultEventsSettings } from '../../../util/eventsSettingsDefaults'

/** Approximate height of the chart time scale strip — used to align the menu with its top edge. */
const HORIZONTAL_SCALE_HEIGHT = 28

type Props = {
    viewKey: string
    scaleWidth: number
    timeScaleHeight?: number
    scaleOnLeft?: boolean
    updateScaleMode: (scaleMode: PriceScaleMode) => void
    updateInvertScale: (invert: boolean) => void
    movePrimaryPriceScale: () => void
    disabledPriceScaleModes: PriceScaleMode[]
    /**
     * Hide the settings button until the chart is hovered (multi-pane layouts
     * only show it on the focused chart). The hover reveal is a CSS rule on the
     * chart container; the button stays visible while its menu is open.
     */
    dimmed?: boolean
}

export const CHART_SCALE_SETTINGS_BUTTON_CLASS = 'chart-scale-settings-button'

type PriceScaleModeProps = {
    value: PriceScaleMode
    onChange: (newValue: PriceScaleMode) => void
    disabledPriceScaleModes: PriceScaleMode[]
}

const defaultScaleOptions: ChartPriceScaleOptions = {
    rightScaleMode: PriceScaleMode.Normal,
    rightScaleVisible: true,
    leftScaleMode: PriceScaleMode.Normal,
    leftScaleVisible: false,
    percentageModeReferenceSourceIndex: 0
}
const defaultInvertScaleOption: ChartInvertScaleOptions = {
    invertScaleMode: false
}

const PriceScaleModeOptions = ({ value, onChange, disabledPriceScaleModes }: PriceScaleModeProps) => {
    const handleClick = useCallback(
        (newValue: PriceScaleMode) => {
            onChange(newValue)
        },
        [onChange]
    )

    const options = useMemo(() => {
        return [
            {
                label: 'Regular',
                value: PriceScaleMode.Normal
            },
            {
                label: 'Percent',
                value: PriceScaleMode.Percentage
            },
            {
                label: 'Indexed to 100',
                value: PriceScaleMode.IndexedTo100
            },
            {
                label: 'Logarithmic',
                value: PriceScaleMode.Logarithmic
            }
        ]
    }, [value])

    return (
        <List sx={{ paddingTop: 0, paddingBottom: 0 }}>
            {options.map((opt) =>
                !disabledPriceScaleModes.includes(opt.value) ? (
                    <ListItem key={opt.value} dense disablePadding>
                        <ListItemButton onClick={() => handleClick(opt.value)} style={{ paddingLeft: '10px' }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>{opt.value === value ? <Check fontSize="small" /> : null}</ListItemIcon>
                            <ListItemText primary={opt.label} />
                        </ListItemButton>
                    </ListItem>
                ) : null
            )}
        </List>
    )
}

/** Submenu popovers render in their own portal (outside the AppMenu paper); this class lets the AppMenu click-away handler treat them as "inside". */
const SUBMENU_PAPER_CLASS = 'chart-scale-submenu'

type SubMenuProps = {
    id: string
    label: string
    container: Element | null
    scaleOnLeft?: boolean
    openId: string | null
    /** Open this submenu now (cancels any pending close). */
    onOpen: (id: string) => void
    /** Close immediately (e.g. backdrop click / escape). */
    onCloseNow: () => void
    /** Schedule a delayed close (shared across submenus so hover transitions don't flicker). */
    onScheduleClose: () => void
    /** Cancel a pending delayed close. */
    onCancelClose: () => void
    children: React.ReactNode
}

const SubMenu = ({ id, label, container, scaleOnLeft, openId, onOpen, onCloseNow, onScheduleClose, onCancelClose, children }: SubMenuProps) => {
    const anchorRef = useRef<HTMLLIElement>(null)
    const open = openId === id

    return (
        <>
            <ListItem
                dense
                disablePadding
                ref={anchorRef}
                onMouseEnter={() => onOpen(id)}
                onMouseLeave={onScheduleClose}
            >
                <ListItemButton style={{ paddingLeft: '10px' }}>
                    <ListItemIcon sx={{ minWidth: 28 }} />
                    <ListItemText primary={label} />
                    <ChevronRight fontSize="small" sx={{ ml: 1, color: 'text.secondary' }} />
                </ListItemButton>
            </ListItem>
            <Menu
                anchorEl={anchorRef.current}
                open={open}
                onClose={onCloseNow}
                hideBackdrop
                disableScrollLock
                disableEnforceFocus
                disableAutoFocus
                disableRestoreFocus
                disableAutoFocusItem
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: scaleOnLeft ? 'right' : 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: scaleOnLeft ? 'left' : 'right',
                }}
                container={container}
                disablePortal={false}
                MenuListProps={{
                    sx: { paddingTop: 0, paddingBottom: 0 },
                    onMouseEnter: onCancelClose,
                    onMouseLeave: onScheduleClose,
                }}
                sx={{ pointerEvents: 'none' }}
                slotProps={{ paper: { className: SUBMENU_PAPER_CLASS, sx: { pointerEvents: 'all' } } }}
            >
                {children}
            </Menu>
        </>
    )
}

const ChartScaleMenu = ({
    viewKey,
    updateScaleMode,
    scaleWidth,
    timeScaleHeight = HORIZONTAL_SCALE_HEIGHT,
    scaleOnLeft = false,
    disabledPriceScaleModes,
    updateInvertScale,
    movePrimaryPriceScale,
    dimmed = false,
}: Props) => {
    const dispatch = useDispatch()
    const [openSubMenuId, setOpenSubMenuId] = useState<string | null>(null)
    const subMenuCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const cancelSubMenuClose = useCallback(() => {
        if (subMenuCloseTimer.current) {
            clearTimeout(subMenuCloseTimer.current)
            subMenuCloseTimer.current = null
        }
    }, [])
    // Single shared close-timer so moving between submenus cancels the sibling's pending close (prevents flicker / instant vanish).
    const openSubMenu = useCallback((id: string) => {
        cancelSubMenuClose()
        setOpenSubMenuId(id)
    }, [cancelSubMenuClose])
    const closeSubMenuNow = useCallback(() => {
        cancelSubMenuClose()
        setOpenSubMenuId(null)
    }, [cancelSubMenuClose])
    const scheduleSubMenuClose = useCallback(() => {
        cancelSubMenuClose()
        subMenuCloseTimer.current = setTimeout(() => setOpenSubMenuId(null), 150)
    }, [cancelSubMenuClose])
    const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ top: number; left: number } | null>(null)
    const settingsButtonRef = useRef<HTMLButtonElement>(null)
    const chartConfig = useSelector(selectChartConfig(viewKey))
    const scalesAndLinesSettings = useSelector(selectChartScalesAndLinesSettings(viewKey))
    const eventsSettings = useSelector(selectChartEventsSettings(viewKey))
    const scaleOptions = useMemo(() => chartConfig?.scaleOptions ?? defaultScaleOptions, [chartConfig])
    const scaleInvertOption = useMemo(() => chartConfig?.scaleInvertOption ?? defaultInvertScaleOption, [chartConfig])
    const [isSettingsOpen, setIsSettingsOpen] = useState(false)
    const selectedChartType = useMemo(() => chartConfig?.chartType ?? ChartType.CANDLESTICK, [chartConfig])
    const priceScaleMode = useMemo(() => {
        return scaleOnLeft ? scaleOptions.leftScaleMode : scaleOptions.rightScaleMode
    }, [scaleOnLeft, scaleOptions.leftScaleMode, scaleOptions.rightScaleMode])

    const moveScaleLabel = scaleOnLeft ? 'Move scale to right' : 'Move scale to left'
    const isEmbedded = useSelector(selectIsEmbedded)
    const isEmbeddedScalper = useMemo(() => {
        return isEmbedded && typeof window !== 'undefined' && !!(window as any).isEmbeddedMode && !!(window as any).scalperManager
    }, [isEmbedded])

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            if (isEmbedded) {
                if (isEmbeddedScalper) {
                    callNative('onScalperChartSettingsClicked')
                } else {
                    callNative('onChartSettingsClicked')
                }
            } else {
                const rect = event.currentTarget.getBoundingClientRect()
                setMenuAnchorPosition({
                    top: rect.bottom - timeScaleHeight,
                    left: scaleOnLeft ? rect.left : rect.right,
                })
            }
        },
        [isEmbedded, isEmbeddedScalper, scaleOnLeft, timeScaleHeight]
    )

    const handleClose = useCallback(() => {
        setMenuAnchorPosition(null)
        cancelSubMenuClose()
        setOpenSubMenuId(null)
    }, [cancelSubMenuClose])

    const handleOpenSettings = useCallback(() => {
        setIsSettingsOpen(true)
        setMenuAnchorPosition(null)
    }, [])

    const handleToggleInvertScale = useCallback(() => {
        const newInvert = !scaleInvertOption?.invertScaleMode
        updateInvertScale(newInvert)
        handleClose()
    }, [scaleInvertOption, updateInvertScale, handleClose])

    const handleCloseSettings = useCallback(() => {
        setIsSettingsOpen(false)
    }, [])

    const handlePriceScaleModeClick = useCallback(
        (mode: PriceScaleMode) => {
            updateScaleMode(mode)
            handleClose()
        },
        [updateScaleMode]
    )

    const updateScalesAndLines = useCallback((partial: Partial<ScalesAndLinesSettings>) => {
        const next = { ...scalesAndLinesSettings, ...partial }
        dispatch(updateChartConfig({
            viewKey,
            config: { scalesAndLinesSettings: next },
            createIfAbsent: false,
        }))
    }, [dispatch, viewKey, scalesAndLinesSettings])

    const updateEventsSettings = useCallback((partial: Partial<EventsSettings>) => {
        dispatch(updateChartConfig({
            viewKey,
            config: {
                eventsSettings: {
                    ...eventsSettings,
                    ...partial,
                },
            },
            createIfAbsent: false,
        }))
    }, [dispatch, viewKey, eventsSettings])

    const showSessionBreaks = eventsSettings?.showSessionBreaks ?? defaultEventsSettings.showSessionBreaks

    const container = useSelector(selectMultiPaneFullScreen)

    return (
        <>
            <NonInteractiveTooltip
                title="Chart Settings"
                arrow
                placement="left-start"
                PopperProps={{
                    container: container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : undefined
                }}
            >
                <Button
                    ref={settingsButtonRef}
                    className={CHART_SCALE_SETTINGS_BUTTON_CLASS}
                    variant="text"
                    color="inherit"
                    disableRipple
                    sx={{
                        opacity: dimmed && !menuAnchorPosition ? 0 : 1,
                        transition: 'opacity 0.15s ease-in',
                        '&:focus-visible': { opacity: 1 },
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bottom: 0,
                        width: scaleWidth,
                        minWidth: 52,
                        height: timeScaleHeight,
                        minHeight: timeScaleHeight,
                        maxHeight: timeScaleHeight,
                        p: 0,
                        borderRadius: 0,
                        zIndex: 1,
                        ...(scaleOnLeft
                            ? { left: 0, right: 'auto' }
                            : { right: 0, left: 'auto' }),
                    }}
                    onClick={handleClick}
                >
                    <SettingsOutlinedIcon sx={{ width: 20, height: 20 }} />
                </Button>
            </NonInteractiveTooltip>
            {!isEmbedded && (
                <AppMenu
                    anchorReference="anchorPosition"
                    anchorPosition={menuAnchorPosition ?? undefined}
                    open={Boolean(menuAnchorPosition)}
                    onClose={handleClose}
                    clickAwayIgnoreRefs={[settingsButtonRef]}
                    clickAwayIgnoreSelectors={[`.${SUBMENU_PAPER_CLASS}`]}
                    disableAutoFocusItem
                    marginThreshold={0}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: scaleOnLeft ? 'left' : 'right',
                    }}
                    container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: scaleOnLeft ? 'left' : 'right',
                    }}
                    MenuListProps={{
                        sx: {
                            paddingTop: 0,
                            paddingBottom: 0,
                            minWidth: 240,
                        }
                    }}
                    slotProps={{
                        paper: {
                            sx: { minWidth: 240 },
                        },
                    }}
                >
                    <ListItem dense disablePadding>
                        <ListItemButton onClick={handleToggleInvertScale} style={{ paddingLeft: '10px' }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>{scaleInvertOption.invertScaleMode ? <Check fontSize="small" /> : null}</ListItemIcon>
                            <ListItemText primary="Invert Scale" />
                        </ListItemButton>
                    </ListItem>

                    <Divider />

                    <PriceScaleModeOptions value={priceScaleMode} onChange={handlePriceScaleModeClick} disabledPriceScaleModes={disabledPriceScaleModes} />

                    {!chartConfig?.overlayPriceScale ? [
                        <Divider key="move-scale-divider-top" />,
                        <ListItem key="move-scale-item" dense disablePadding>
                            <ListItemButton
                                style={{ paddingLeft: '10px' }}
                                onClick={() => {
                                    movePrimaryPriceScale()
                                    handleClose()
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 28 }} />
                                <ListItemText primary={moveScaleLabel} />
                            </ListItemButton>
                        </ListItem>,
                        <Divider key="move-scale-divider-bottom" />,
                    ] : (
                        <Divider key="overlay-scale-divider" />
                    )}

                    <SubMenu id="labels" label="Labels" scaleOnLeft={scaleOnLeft} openId={openSubMenuId} onOpen={openSubMenu} onCloseNow={closeSubMenuNow} onScheduleClose={scheduleSubMenuClose} onCancelClose={cancelSubMenuClose} container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showSymbolValue: !(scalesAndLinesSettings?.showSymbolValue ?? true) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showSymbolValue ?? true) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Symbol last price label" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showHighLowLabel: !(scalesAndLinesSettings?.showHighLowLabel ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showHighLowLabel ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Visible range high and low price labels" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showPrevDayHighLowLabel: !(scalesAndLinesSettings?.showPrevDayHighLowLabel ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showPrevDayHighLowLabel ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Previous day high and low price labels" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showCurrentDayHighLowLabel: !(scalesAndLinesSettings?.showCurrentDayHighLowLabel ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showCurrentDayHighLowLabel ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Current day high and low price labels" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showPrevDayCloseLabel: !(scalesAndLinesSettings?.showPrevDayCloseLabel ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showPrevDayCloseLabel ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Previous day close price label" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showTodayOpenLabel: !(scalesAndLinesSettings?.showTodayOpenLabel ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showTodayOpenLabel ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Current day open price label" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showIndicatorLabel: !(scalesAndLinesSettings?.showIndicatorLabel ?? true) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showIndicatorLabel ?? true) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Indicator name labels" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showIndicatorValue: !(scalesAndLinesSettings?.showIndicatorValue ?? true) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showIndicatorValue ?? true) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Indicator value labels" />
                            </ListItemButton>
                        </ListItem>
                        {!isTransformedChartType(selectedChartType) ? (
                            <ListItem dense disablePadding>
                                <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showCountdown: !(scalesAndLinesSettings?.showCountdown ?? true) })}>
                                    <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showCountdown ?? true) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                    <ListItemText primary="Countdown to bar close" />
                                </ListItemButton>
                            </ListItem>
                        ) : null}
                        <Divider />
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ alignLabels: !(scalesAndLinesSettings?.alignLabels ?? true) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.alignLabels ?? true) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="No overlapping labels" />
                            </ListItemButton>
                        </ListItem>
                    </SubMenu>

                    <SubMenu id="lines" label="Lines" scaleOnLeft={scaleOnLeft} openId={openSubMenuId} onOpen={openSubMenu} onCloseNow={closeSubMenuNow} onScheduleClose={scheduleSubMenuClose} onCancelClose={cancelSubMenuClose} container={container ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID) : null}>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showSymbolLine: !(scalesAndLinesSettings?.showSymbolLine ?? true) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showSymbolLine ?? true) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Symbol last price line" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showHighLowLine: !(scalesAndLinesSettings?.showHighLowLine ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showHighLowLine ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Visible range high and low price lines" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showPrevDayHighLowLine: !(scalesAndLinesSettings?.showPrevDayHighLowLine ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showPrevDayHighLowLine ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Previous day high and low price lines" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showCurrentDayHighLowLine: !(scalesAndLinesSettings?.showCurrentDayHighLowLine ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showCurrentDayHighLowLine ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Current day high and low price lines" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showPrevDayCloseLine: !(scalesAndLinesSettings?.showPrevDayCloseLine ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showPrevDayCloseLine ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Previous day close price line" />
                            </ListItemButton>
                        </ListItem>
                        <ListItem dense disablePadding>
                            <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showTodayOpenLine: !(scalesAndLinesSettings?.showTodayOpenLine ?? false) })}>
                                <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showTodayOpenLine ?? false) ? <Check fontSize="small" /> : null}</ListItemIcon>
                                <ListItemText primary="Current day open price line" />
                            </ListItemButton>
                        </ListItem>
                    </SubMenu>

                    <ListItem dense disablePadding>
                        <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateScalesAndLines({ showPlusButton: !(scalesAndLinesSettings?.showPlusButton ?? true) })}>
                            <ListItemIcon sx={{ minWidth: 28 }}>{(scalesAndLinesSettings?.showPlusButton ?? true) ? <Check fontSize="small" /> : null}</ListItemIcon>
                            <ListItemText primary="Plus button" />
                        </ListItemButton>
                    </ListItem>

                    <Divider />

                    <ListItem dense disablePadding>
                        <ListItemButton style={{ paddingLeft: '10px' }} onClick={() => updateEventsSettings({ showSessionBreaks: !showSessionBreaks })}>
                            <ListItemIcon sx={{ minWidth: 28 }}>{showSessionBreaks ? <Check fontSize="small" /> : null}</ListItemIcon>
                            <ListItemText primary="Session breaks" />
                        </ListItemButton>
                    </ListItem>

                    <Divider />

                    <ListItem dense disablePadding>
                        <ListItemButton onClick={handleOpenSettings} style={{ paddingLeft: '10px' }}>
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                <SettingsOutlinedIcon sx={{ width: 24, height: 24 }} />
                            </ListItemIcon>
                            <ListItemText primary="More Settings..." />
                        </ListItemButton>
                    </ListItem>
                </AppMenu>
            )}
            <ChartSettingsDialogs open={isSettingsOpen} onClose={handleCloseSettings} viewKey={viewKey} chartType={selectedChartType} />
        </>
    )
}

export default memo(ChartScaleMenu)
